import React, { useCallback, useEffect, useState } from 'react';
import { Search, UserRound, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import AdminUserProfileModal from './AdminUserProfileModal';
import AdminUsersListModal from './AdminUsersListModal';
import AdminDemographicsSection from './AdminDemographicsSection';
import { DEFAULT_PROFILE_PIC, resolveProfilePic, handleProfilePicError } from '../lib/avatar';


const getTopReferrerProfilePic = (user = {}) => (
  user.profilePic
  || user.profilePicture
  || user.profileImage
  || user.avatar
  || user.photoURL
  || user.photoUrl
  || user.image
  || user.picture
  || ''
);

const getInitial = (user = {}) => {
  const label = user.name || user.username || '?';
  return label.replace(/^@/, '').trim().charAt(0).toUpperCase() || '?';
};

const hasUploadedProfilePic = (profilePic) => {
  const resolved = resolveProfilePic(profilePic);
  return Boolean(profilePic) && resolved !== DEFAULT_PROFILE_PIC;
};

const AdminUsersSectionV2 = () => {
  const [query, setQuery] = useState(() => {
    try { return sessionStorage.getItem('adminUsersSearchText') || ''; } catch { return ''; }
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(() => {
    try { return sessionStorage.getItem('adminUsersSelectedId') || null; } catch { return null; }
  });
  const [message, setMessage] = useState('');

  const [analytics, setAnalytics] = useState({
    todayNewUsers: 0,
    todayActive: 0,
    last7DaysUsers: 0,
    sevenDayActiveToday: 0,
    last30DaysUsers: 0,
    thirtyDayActiveToday: 0,
    rangeUsers: 0,
    rangeActiveToday: 0,
  });
  const [analyticsFrom, setAnalyticsFrom] = useState('');
  const [analyticsTo, setAnalyticsTo] = useState('');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [showReferralsStats, setShowReferralsStats] = useState(false);
  const [refFrom, setRefFrom] = useState('');
  const [refTo, setRefTo] = useState('');
  const [refStatsLoading, setRefStatsLoading] = useState(false);
  const [refStats, setRefStats] = useState({ todayReferrals: 0, rangeReferrals: 0 });

  const [topRefLimit, setTopRefLimit] = useState(20);
  const [topRefLoading, setTopRefLoading] = useState(false);
  const [topRefUsers, setTopRefUsers] = useState([]);

  // "Click on stat card" → shows a list of users for that period.
  // We keep the selected period + label so the modal can display them.
  const [listModalPeriod, setListModalPeriod] = useState(null);
  const [listModalTitle, setListModalTitle] = useState('');

  const openUsersListForPeriod = (period, label) => {
    setListModalPeriod(period);
    setListModalTitle(label);
  };

  const closeUsersListModal = () => {
    setListModalPeriod(null);
    setListModalTitle('');
  };

  const handleSelectUserFromList = (userId) => {
    if (!userId) return;
    closeUsersListModal();
    setSelectedUserId(userId);
  };

  const loadUsers = useCallback(async (searchText) => {
    const username = (searchText || '').trim();
    if (!username) {
      setUsers([]);
      setUsersLoading(false);
      return;
    }

    setUsersLoading(true);
    setMessage('');
    try {
      const { data } = await api.searchAdminUsers({ username, limit: 100 });
      setUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (error) {
      setMessage(error?.response?.data?.detail || error.message || 'Failed to search users');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setUsers([]);
      return;
    }

    const timer = setTimeout(() => {
      loadUsers(trimmed);
    }, 250);

    return () => clearTimeout(timer);
  }, [loadUsers, query]);

  const loadUsersAnalytics = useCallback(async (params = {}) => {
    setAnalyticsLoading(true);
    try {
      const { data } = await api.getAdminUsersAnalytics(params);
      setAnalytics({
        todayNewUsers: data?.todayNewUsers ?? data?.todayTotal ?? 0,
        todayActive: data?.todayActive ?? 0,
        last7DaysUsers: data?.last7DaysUsers ?? data?.sevenDayTotal ?? 0,
        sevenDayActiveToday: data?.sevenDayActiveToday ?? 0,
        last30DaysUsers: data?.last30DaysUsers ?? data?.thirtyDayTotal ?? 0,
        thirtyDayActiveToday: data?.thirtyDayActiveToday ?? 0,
        rangeUsers: data?.rangeUsers ?? data?.rangeTotal ?? 0,
        rangeActiveToday: data?.rangeActiveToday ?? 0,
      });
    } catch (error) {
      setMessage(error?.response?.data?.detail || error.message || 'Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const loadReferralStats = useCallback(async (params = {}) => {
    setRefStatsLoading(true);
    try {
      const { data } = await api.getAdminReferralsAnalytics(params);
      setRefStats({
        todayReferrals: data?.todayReferrals || 0,
        rangeReferrals: data?.rangeReferrals || 0,
      });
    } catch (error) {
      setMessage(error?.response?.data?.detail || error.message || 'Failed to load referral stats');
    } finally {
      setRefStatsLoading(false);
    }
  }, []);

  const loadTopReferrers = useCallback(async (limit = 20) => {
    setTopRefLoading(true);
    try {
      const { data } = await api.getAdminTopReferrers(limit);
      setTopRefUsers(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      setMessage(error?.response?.data?.detail || error.message || 'Failed to load top referrals');
    } finally {
      setTopRefLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsersAnalytics();
    loadTopReferrers(20);
  }, [loadUsersAnalytics, loadTopReferrers]);

  useEffect(() => {
    const scheduleDrawResetRefresh = () => {
      const now = new Date();
      const nextReset = new Date(now);
      // Harday reset is 19:00 IST, which is 13:30 UTC.
      nextReset.setUTCHours(13, 30, 5, 0);
      if (now >= nextReset) {
        nextReset.setUTCDate(nextReset.getUTCDate() + 1);
      }
      return window.setTimeout(() => {
        loadUsersAnalytics({ fromDate: analyticsFrom || undefined, toDate: analyticsTo || undefined });
        if (listModalPeriod) {
          // Remount the period modal so it refetches badges/counts after reset.
          setListModalPeriod(null);
          window.setTimeout(() => setListModalPeriod(listModalPeriod), 0);
        }
      }, Math.max(nextReset.getTime() - now.getTime(), 1000));
    };

    const timer = scheduleDrawResetRefresh();
    return () => window.clearTimeout(timer);
  }, [analyticsFrom, analyticsTo, listModalPeriod, loadUsersAnalytics]);

  // Persist search text + selected user across page refreshes so admin
  // stays in the same user editing context after Save → Refresh flow.
  useEffect(() => {
    try {
      if (query) {
        sessionStorage.setItem('adminUsersSearchText', query);
      } else {
        sessionStorage.removeItem('adminUsersSearchText');
      }
    } catch {}
  }, [query]);

  useEffect(() => {
    try {
      if (selectedUserId) {
        sessionStorage.setItem('adminUsersSelectedId', selectedUserId);
      } else {
        sessionStorage.removeItem('adminUsersSelectedId');
      }
    } catch {}
  }, [selectedUserId]);

  const analyticsLabel = analyticsLoading ? '...' : `${analytics.rangeUsers}`;
  const analyticsActiveClass = 'mt-0.5 text-[11px] font-bold leading-none text-green-600';

  const handleUserUpdated = (updatedUser) => {
    setUsers((prev) => prev.map((user) => (user.id === updatedUser.id ? { ...user, ...updatedUser } : user)));
  };

  const handleUserDeleted = (deletedUserId) => {
    if (!deletedUserId) return;
    setUsers((prev) => prev.filter((user) => user.id !== deletedUserId));
    setSelectedUserId((prev) => (prev === deletedUserId ? null : prev));
    try {
      sessionStorage.removeItem('adminUsersSelectedId');
    } catch {}
    setMessage('User deleted.');
    // Refresh user analytics so the count reflects the deletion immediately.
    loadUsersAnalytics({
      fromDate: analyticsFrom || undefined,
      toDate: analyticsTo || undefined,
    });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-1.5">
        <button
          type="button"
          data-testid="admin-users-card-today"
          onClick={() => openUsersListForPeriod('today', 'Today')}
          className="rounded-[10px] border border-[#ececec] bg-white px-2 py-1 text-left hover:border-[#9333EA]/40 active:scale-[0.98] transition-all"
        >
          <p className="text-[10px] text-[#666666]">Today</p>
          <p className="text-sm font-bold text-[#111111]">{analytics.todayNewUsers}</p>
          <p className={analyticsActiveClass}>{analytics.todayActive ?? 0}</p>
        </button>
        <button
          type="button"
          data-testid="admin-users-card-7d"
          onClick={() => openUsersListForPeriod('7d', 'Last 7 Days')}
          className="rounded-[10px] border border-[#ececec] bg-white px-2 py-1 text-left hover:border-[#9333EA]/40 active:scale-[0.98] transition-all"
        >
          <p className="text-[10px] text-[#666666]">7d</p>
          <p className="text-sm font-bold text-[#111111]">{analytics.last7DaysUsers}</p>
          <p className={analyticsActiveClass}>{analytics.sevenDayActiveToday ?? 0}</p>
        </button>
        <button
          type="button"
          data-testid="admin-users-card-30d"
          onClick={() => openUsersListForPeriod('30d', 'Last 30 Days')}
          className="rounded-[10px] border border-[#ececec] bg-white px-2 py-1 text-left hover:border-[#9333EA]/40 active:scale-[0.98] transition-all"
        >
          <p className="text-[10px] text-[#666666]">30d</p>
          <p className="text-sm font-bold text-[#111111]">{analytics.last30DaysUsers}</p>
          <p className={analyticsActiveClass}>{analytics.thirtyDayActiveToday ?? 0}</p>
        </button>
        <button
          type="button"
          data-testid="admin-users-card-range"
          onClick={() => openUsersListForPeriod('range', 'Selected Range')}
          className="rounded-[10px] border border-[#ececec] bg-white px-2 py-1 text-left hover:border-[#9333EA]/40 active:scale-[0.98] transition-all"
        >
          <p className="text-[10px] text-[#666666]">Range</p>
          <p className="text-sm font-bold text-[#111111]">{analyticsLabel}</p>
          <p className={analyticsActiveClass}>{analytics.rangeActiveToday ?? 0}</p>
        </button>
      </div>

      <div className="rounded-[12px] border border-[#ececec] bg-white p-2 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={analyticsFrom} onChange={(e) => setAnalyticsFrom(e.target.value)} className="h-8 rounded-[9px] border border-[#ececec] px-2 text-xs" />
          <input type="date" value={analyticsTo} onChange={(e) => setAnalyticsTo(e.target.value)} className="h-8 rounded-[9px] border border-[#ececec] px-2 text-xs" />
        </div>
        <button
          onClick={() => loadUsersAnalytics({ fromDate: analyticsFrom || undefined, toDate: analyticsTo || undefined })}
          className="h-8 w-full rounded-[9px] bg-[#9333EA] text-white text-xs font-semibold"
        >
          {analyticsLoading ? 'Loading...' : 'Refresh User Analytics'}
        </button>
      </div>

      <div className="rounded-[14px] bg-[#FEF3C7] border border-[#FDE68A] p-3">
        <div className="rounded-[12px] border border-[#FDE68A] bg-white p-3 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 text-[#666666] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users by username..."
              className="w-full h-10 rounded-[12px] border border-[#ececec] pl-9 pr-3 text-sm bg-white"
            />
          </div>

          {usersLoading ? (
            <p className="text-xs text-[#666666] font-['DM_Sans']">Searching users...</p>
          ) : query.trim() === '' ? (
            <p className="text-xs text-[#666666] font-['DM_Sans']">Type something in search to see matching users.</p>
          ) : users.length === 0 ? (
            <p className="text-xs text-[#666666] font-['DM_Sans']">No users found for this search.</p>
          ) : (
            <div className="space-y-2">
              {users.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => setSelectedUserId(user.id)}
                  className="w-full rounded-[14px] border border-[#ececec] bg-white px-3 py-3 flex items-center gap-3 text-left hover:border-[#9333EA]/30 transition-colors"
                >
                  {user.profilePic ? (
                    <img src={user.profilePic} alt={user.username} className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-[#9333EA]/10 flex items-center justify-center">
                      <UserRound className="w-5 h-5 text-[#9333EA]" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#000000] font-['Outfit'] truncate">{user.username}</p>
                    <p className="text-xs text-[#666666] font-['DM_Sans'] truncate">{user.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#000000] font-['Outfit']">{Number(user.credits || 0)}</p>
                    <p className="text-[10px] text-[#666666] font-['DM_Sans']">credits</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-[12px] border border-[#ececec] bg-white p-3 space-y-2">
        <button
          type="button"
          onClick={() => setShowReferralsStats((prev) => !prev)}
          aria-expanded={showReferralsStats}
          className="w-full flex items-center justify-between text-left text-sm font-semibold text-[#000000] font-['Outfit']"
        >
          <span>Referral Analytics</span>
          <ChevronRight
            className={`w-4 h-4 text-[#666666] transition-transform ${showReferralsStats ? 'rotate-90' : ''}`}
          />
        </button>
        {showReferralsStats && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={refFrom} onChange={(e) => setRefFrom(e.target.value)} className="h-8 rounded-[9px] border border-[#ececec] px-2 text-xs" />
              <input type="date" value={refTo} onChange={(e) => setRefTo(e.target.value)} className="h-8 rounded-[9px] border border-[#ececec] px-2 text-xs" />
            </div>
            <button
              onClick={() => loadReferralStats({ fromDate: refFrom || undefined, toDate: refTo || undefined })}
              className="h-8 w-full rounded-[9px] bg-[#111111] text-white text-xs font-semibold"
            >
              {refStatsLoading ? 'Loading...' : 'Refresh Referral Stats'}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-[10px] border border-[#ececec] bg-[#fafafa] px-2 py-2">
                <p className="text-[10px] text-[#666666]">Today Referrals</p>
                <p className="text-sm font-bold text-[#111111]">{refStats.todayReferrals}</p>
              </div>
              <div className="rounded-[10px] border border-[#ececec] bg-[#fafafa] px-2 py-2">
                <p className="text-[10px] text-[#666666]">Range Referrals</p>
                <p className="text-sm font-bold text-[#111111]">{refStats.rangeReferrals}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input type="number" min="1" value={topRefLimit} onChange={(e) => setTopRefLimit(e.target.value)} className="h-8 w-24 rounded-[9px] border border-[#ececec] px-2 text-xs" />
              <button onClick={() => loadTopReferrers(Number(topRefLimit) || 20)} className="h-8 flex-1 rounded-[9px] bg-[#111111] text-white text-xs font-semibold">
                {topRefLoading ? 'Loading...' : 'Top Referrers'}
              </button>
            </div>
            <div className="space-y-2">
          {topRefUsers.map((user, index) => {
            const referralCount = Number(user.totalReferrals ?? user.activeReferrals ?? 0);
            const referrerId = user.userId || user.id;
            const profilePic = getTopReferrerProfilePic(user);
            const showProfilePic = hasUploadedProfilePic(profilePic);
            const displayName = user.username || user.name || 'Unknown user';
            return (
              <button
                key={referrerId || user.username || index}
                type="button"
                onClick={() => referrerId && setSelectedUserId(referrerId)}
                className="w-full rounded-[10px] border border-[#ececec] bg-[#fafafa] px-3 py-2 flex items-center gap-3 text-left hover:border-[#9333EA]/30 active:scale-[0.99] transition-all"
              >
                <div className="shrink-0 h-10 w-10 rounded-full overflow-hidden bg-[#9333EA]/10 flex items-center justify-center border border-[#ececec]">
                  {showProfilePic ? (
                    <img
                      src={resolveProfilePic(profilePic)}
                      alt={displayName}
                      onError={handleProfilePicError}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-bold text-[#9333EA] font-['Outfit']">{getInitial(user)}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#111111] truncate">{index + 1}. {displayName}</p>
                  <p className="text-xs text-[#666666] truncate">Earnings: {Number(user.referralEarningsCredits || 0)}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-bold text-[#111111]">{referralCount}</p>
                  <p className="text-[10px] text-[#666666]">referrals</p>
                </div>
              </button>
            );
          })}
            </div>
          </>
        )}
      </div>

      <AdminDemographicsSection onSelectUser={setSelectedUserId} />

      {message && <p className="text-xs text-red-600 font-['DM_Sans']">{message}</p>}

      <AdminUserProfileModal
        userId={selectedUserId}
        isOpen={Boolean(selectedUserId)}
        onClose={() => setSelectedUserId(null)}
        onUserUpdated={handleUserUpdated}
        onUserDeleted={handleUserDeleted}
      />

      <AdminUsersListModal
        isOpen={Boolean(listModalPeriod)}
        onClose={closeUsersListModal}
        period={listModalPeriod || 'today'}
        title={listModalTitle}
        fromDate={analyticsFrom}
        toDate={analyticsTo}
        onSelectUser={handleSelectUserFromList}
      />
    </div>
  );
};

export default AdminUsersSectionV2;
